import { ChipProps } from '@mi-front/ui';

/** Match vs schedule counts used to derive Completo / Parcial / Sin emisión. */
export type ScheduleStatusCoverage = {
  matchesCount: number;
  matchesWithScheduleCount: number;
  matchesWithScheduleOffToleranceCount?: number;
  schedulesCount: number;
};

export type ScheduleStatusKind = 'missingEmission' | 'partial' | 'none' | 'complete';

/** Coverage kinds plus the off-tolerance flag, used by the column filter. */
export type ScheduleStatusFilterValue = ScheduleStatusKind | 'offTolerance';

/**
 * Derives coverage status from match vs schedule counts.
 * Off-tolerance is a separate flag and does not change Completo vs Parcial.
 */
export function getScheduleStatusKind(status: ScheduleStatusCoverage): ScheduleStatusKind {
  const { matchesCount, matchesWithScheduleCount, schedulesCount } = status;

  if (matchesCount === 0 && schedulesCount > 0) {
    return 'missingEmission';
  }

  if (matchesCount === 0) {
    return 'none';
  }

  if (matchesWithScheduleCount >= schedulesCount) {
    return 'complete';
  }

  return 'partial';
}

/** True when at least one scheduled match is outside the allowed tolerance. */
export function hasScheduleStatusOffTolerance(status: ScheduleStatusCoverage): boolean {
  return (status.matchesWithScheduleOffToleranceCount ?? 0) > 0;
}

/** Chip color aligned with coverage (off-tolerance is shown as an icon, not a color). */
export function getScheduleStatusChipType(kind: ScheduleStatusKind): ChipProps['type'] {
  switch (kind) {
    case 'missingEmission':
      return 'danger';
    case 'none':
      return 'light';
    case 'complete':
      return 'success';
    case 'partial':
      return 'yellow';
  }
}

/**
 * Lower rank is more severe so ascending sort puts problems first.
 * Completo with off-tolerance sits just above clean Completo.
 */
export function getScheduleStatusSortRank(status: ScheduleStatusCoverage): number {
  const kind = getScheduleStatusKind(status);

  switch (kind) {
    case 'missingEmission':
      return 0;
    case 'partial':
      return 1;
    case 'none':
      return 2;
    case 'complete':
      return hasScheduleStatusOffTolerance(status) ? 3 : 4;
  }
}

/** Whether a row matches the selected status filter value. */
export function matchesScheduleStatusFilter(
  status: ScheduleStatusCoverage,
  filterValue: ScheduleStatusFilterValue | '' | null | undefined,
): boolean {
  if (filterValue == null || filterValue === '') {
    return true;
  }

  if (filterValue === 'offTolerance') {
    return hasScheduleStatusOffTolerance(status);
  }

  return getScheduleStatusKind(status) === filterValue;
}

/** Filter options in severity order. Off-tolerance is a flag, not a coverage kind. */
export const SCHEDULE_STATUS_FILTER_ORDER: ScheduleStatusFilterValue[] = [
  'missingEmission',
  'partial',
  'offTolerance',
  'none',
  'complete',
];
